import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SpeedIcon from "@mui/icons-material/Speed";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DevicesIcon from "@mui/icons-material/Devices";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import Layout from "../../components/layout/Layout";
import { api } from "../../api/api";
import { isSuperAdmin } from "../../utils/accessControl";
import "./screenBandwidth.css";
import { tr } from "../../i18n";

function toInputDate(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function defaultStartDate() {
  const date = new Date();
  date.setDate(date.getDate() - 6);
  return toInputDate(date);
}

function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = value / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

function formatRangeLabel(start, end) {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

function screenLabel(screen) {
  return screen?.name || screen?.mac || `Code ${screen?.code || "Unknown"}`;
}

function contentTypeClass(type = "") {
  const key = String(type).toLowerCase();
  if (key.includes("video")) return "bandwidth-type-chip--video";
  if (key.includes("image") || key.includes("photo")) return "bandwidth-type-chip--image";
  if (key.includes("pdf")) return "bandwidth-type-chip--pdf";
  if (key.includes("url") || key.includes("web")) return "bandwidth-type-chip--url";
  if (key.includes("audio")) return "bandwidth-type-chip--audio";
  return "bandwidth-type-chip--default";
}

function bytesBadgeClass(bytes) {
  const value = Number(bytes) || 0;
  if (value >= 100 * 1024 * 1024) return "bandwidth-bytes-badge--high";
  if (value >= 10 * 1024 * 1024) return "bandwidth-bytes-badge--mid";
  return "bandwidth-bytes-badge--low";
}

function barFillClass(index) {
  const classes = [
    "bandwidth-bar-fill--teal",
    "bandwidth-bar-fill--purple",
    "bandwidth-bar-fill--blue",
    "bandwidth-bar-fill--amber",
  ];
  return classes[index % classes.length];
}

function ScreenBandwidth({ user }) {
  const [startDate, setStartDate] = useState(defaultStartDate());
  const [endDate, setEndDate] = useState(toInputDate(new Date()));
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [screens, setScreens] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState({ bytes: 0, hits: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const loadScreens = async () => {
    if (!isSuperAdmin(user)) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/bandwidth/screens", {
        params: {
          startDate,
          endDate,
          search,
          page: page + 1,
          limit: rowsPerPage,
        },
      });
      setScreens(Array.isArray(response.data?.screens) ? response.data.screens : []);
      setPagination(response.data?.pagination || null);
      setSummary(response.data?.summary || { bytes: 0, hits: 0 });
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load screen bandwidth.");
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (screen) => {
    setSelectedScreen(screen);
    setDetail(null);
    setDetailError("");
    setDetailLoading(true);
    try {
      const response = await api.get(`/bandwidth/screens/${screen._id}`, {
        params: { startDate, endDate },
      });
      setDetail(response.data);
    } catch (err) {
      setDetailError(err?.response?.data?.message || "Could not load bandwidth detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadScreens();
  }, [user?._id, startDate, endDate, search, page, rowsPerPage]);

  const applySearch = () => {
    setPage(0);
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setStartDate(defaultStartDate());
    setEndDate(toInputDate(new Date()));
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const maxDailyBytes = Math.max(...(detail?.daily || []).map((row) => row.bytes || 0), 1);

  if (!isSuperAdmin(user)) {
    return (
      <Layout title={tr("Screen Bandwidth")} user={user}>
        <Box className="bandwidth-page">
          <Alert severity="warning">{tr("Only super admin can view screen bandwidth.")}</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title={tr("Screen Bandwidth")} user={user}>
      <Box className="bandwidth-page">
        <header className="bandwidth-hero">
          <Typography component="h1" className="bandwidth-title">
            {tr("Screen Bandwidth")}
          </Typography>
          <Typography className="bandwidth-subtitle">
            {tr("Monitor how much media each screen pulls from the server. Click a row to see daily usage and top assets.")}
          </Typography>
        </header>

        <section className="bandwidth-stat-row" aria-label={tr("Bandwidth overview")}>
          <article className="bandwidth-stat-card bandwidth-stat-card--teal">
            <div className="bandwidth-stat-icon-wrap">
              <SpeedIcon fontSize="small" />
            </div>
            <Typography className="bandwidth-stat-label">{tr("Total Bandwidth")}</Typography>
            <Typography className="bandwidth-stat-value">
              {loading ? "…" : formatBytes(summary.bytes)}
            </Typography>
            <Typography className="bandwidth-stat-help">
              {formatRangeLabel(startDate, endDate)}
            </Typography>
          </article>

          <article className="bandwidth-stat-card bandwidth-stat-card--purple">
            <div className="bandwidth-stat-icon-wrap">
              <CloudDownloadIcon fontSize="small" />
            </div>
            <Typography className="bandwidth-stat-label">{tr("Media Requests")}</Typography>
            <Typography className="bandwidth-stat-value">
              {loading ? "…" : (summary.hits || 0).toLocaleString()}
            </Typography>
            <Typography className="bandwidth-stat-help">
              {tr("Uploaded media served from this server")}
            </Typography>
          </article>

          <article className="bandwidth-stat-card bandwidth-stat-card--bronze">
            <div className="bandwidth-stat-icon-wrap">
              <DevicesIcon fontSize="small" />
            </div>
            <Typography className="bandwidth-stat-label">{tr("Screens")}</Typography>
            <Typography className="bandwidth-stat-value">
              {loading ? "…" : (pagination?.totalItems || 0).toLocaleString()}
            </Typography>
            <Typography className="bandwidth-stat-help">
              {tr("Matching your filters")}
            </Typography>
          </article>
        </section>

        <Paper className="bandwidth-filter-bar" elevation={0}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <TextField
              label={tr("From")}
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => {
                setPage(0);
                setStartDate(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={tr("To")}
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => {
                setPage(0);
                setEndDate(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={tr("Search screen")}
              size="small"
              placeholder={tr("Name, MAC, or code")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch();
              }}
              sx={{ minWidth: 200 }}
            />
          </Stack>
          <Stack className="bandwidth-filter-actions" direction="row" spacing={1}>
            <span className={`bandwidth-filter-pill${search ? " bandwidth-filter-pill--active" : ""}`}>
              {search ? `Filtered: "${search}"` : tr("All screens")}
            </span>
            <Button
              variant="contained"
              className="bandwidth-filter-btn bandwidth-filter-btn--primary"
              startIcon={<SearchIcon />}
              onClick={applySearch}
            >
              {tr("Apply")}
            </Button>
            <Button
              variant="outlined"
              className="bandwidth-filter-btn"
              startIcon={<RestartAltIcon />}
              onClick={clearFilters}
            >
              {tr("Reset")}
            </Button>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper className="bandwidth-table-card" elevation={0}>
          <Box className="bandwidth-table-header">
            <Typography className="bandwidth-table-title">{tr("Screen Usage")}</Typography>
            <Typography className="bandwidth-table-meta">
              {loading
                ? tr("Loading…")
                : `${pagination?.totalItems || 0} screen${(pagination?.totalItems || 0) === 1 ? "" : "s"} · sorted by bandwidth`}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tr("Screen")}</TableCell>
                  <TableCell>{tr("MAC")}</TableCell>
                  <TableCell>{tr("Organization")}</TableCell>
                  <TableCell>{tr("Last Active")}</TableCell>
                  <TableCell align="right">{tr("Requests")}</TableCell>
                  <TableCell align="right">{tr("Bandwidth")}</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} sx={{ color: "#0b6e6e" }} />
                    </TableCell>
                  </TableRow>
                ) : screens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box className="bandwidth-empty-state">
                        <Box className="bandwidth-empty-icon">
                          <SignalCellularAltIcon />
                        </Box>
                        <Typography className="bandwidth-empty-title">
                          {tr("No bandwidth data found")}
                        </Typography>
                        <Typography className="bandwidth-empty-help">
                          {tr("Try widening the date range or clearing your search filters.")}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  screens.map((screen) => (
                    <TableRow
                      key={screen._id}
                      hover
                      className="bandwidth-clickable-row"
                      onClick={() => loadDetail(screen)}
                    >
                      <TableCell>
                        <Typography className="bandwidth-screen-name">{screenLabel(screen)}</Typography>
                        <Typography className="bandwidth-screen-subtitle">
                          {tr("Code:")} {screen?.code || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>{screen?.mac || "-"}</TableCell>
                      <TableCell>{screen?.user?.username || "-"}</TableCell>
                      <TableCell>
                        {screen?.lastActive ? new Date(screen.lastActive).toLocaleString() : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <span className="bandwidth-hits-badge">{screen?.bandwidthHits || 0}</span>
                      </TableCell>
                      <TableCell align="right">
                        <span
                          className={`bandwidth-bytes-badge ${bytesBadgeClass(screen?.bandwidthBytes)}`}
                        >
                          {formatBytes(screen?.bandwidthBytes)}
                        </span>
                      </TableCell>
                      <TableCell padding="checkbox" align="right">
                        <ChevronRightIcon className="bandwidth-row-chevron" fontSize="small" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination?.totalItems || 0}
            page={page}
            onPageChange={(event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>

        <Dialog
          className="bandwidth-dialog"
          open={Boolean(selectedScreen)}
          onClose={() => setSelectedScreen(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle className="bandwidth-dialog-title" component="div">
            <Box>
              <Typography className="bandwidth-dialog-title-text">
                {screenLabel(selectedScreen)}
              </Typography>
              <Typography className="bandwidth-dialog-subtitle">
                {tr("Bandwidth detail ·")} {formatRangeLabel(startDate, endDate)}
              </Typography>
            </Box>
            <IconButton
              aria-label={tr("Close")}
              onClick={() => setSelectedScreen(null)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent className="bandwidth-dialog-content" dividers>
            {detailLoading ? (
              <Box className="bandwidth-detail-loading">
                <CircularProgress size={30} sx={{ color: "#0b6e6e" }} />
              </Box>
            ) : detailError ? (
              <Alert severity="error">{detailError}</Alert>
            ) : (
              <>
                <Box className="bandwidth-detail-summary">
                  <Box className="bandwidth-detail-stat bandwidth-detail-stat--teal">
                    <Typography className="bandwidth-summary-label">{tr("Total Bandwidth")}</Typography>
                    <Typography className="bandwidth-detail-value bandwidth-detail-value--teal">
                      {formatBytes(detail?.summary?.bytes)}
                    </Typography>
                  </Box>
                  <Box className="bandwidth-detail-stat bandwidth-detail-stat--purple">
                    <Typography className="bandwidth-summary-label">{tr("Media Requests")}</Typography>
                    <Typography className="bandwidth-detail-value bandwidth-detail-value--purple">
                      {(detail?.summary?.hits || 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>

                <Typography className="bandwidth-section-title bandwidth-section-title--daily">
                  {tr("Daily Usage")}
                </Typography>
                <TableContainer component={Paper} className="bandwidth-detail-table" elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{tr("Date")}</TableCell>
                        <TableCell align="right">{tr("Requests")}</TableCell>
                        <TableCell align="right">{tr("Bandwidth")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detail?.daily || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 3, color: "#64748b" }}>
                            {tr("No bandwidth recorded in this range.")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        detail.daily.map((row, index) => (
                          <TableRow key={row.date}>
                            <TableCell>
                              <span className="bandwidth-date-pill">{formatDate(row.date)}</span>
                            </TableCell>
                            <TableCell align="right">
                              <span className="bandwidth-hits-badge">{row.hits}</span>
                            </TableCell>
                            <TableCell align="right">
                              <Box className="bandwidth-bar-cell" sx={{ justifyContent: "flex-end" }}>
                                <Box className="bandwidth-bar-track">
                                  <Box
                                    className={`bandwidth-bar-fill ${barFillClass(index)}`}
                                    sx={{ width: `${((row.bytes || 0) / maxDailyBytes) * 100}%` }}
                                  />
                                </Box>
                                <span className={`bandwidth-bytes-badge ${bytesBadgeClass(row.bytes)}`}>
                                  {formatBytes(row.bytes)}
                                </span>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography className="bandwidth-section-title bandwidth-section-title--assets">
                  {tr("Top Assets")}
                </Typography>
                <TableContainer component={Paper} className="bandwidth-detail-table" elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{tr("Asset")}</TableCell>
                        <TableCell>{tr("Type")}</TableCell>
                        <TableCell align="right">{tr("Requests")}</TableCell>
                        <TableCell align="right">{tr("Bandwidth")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detail?.byContent || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 3, color: "#64748b" }}>
                            {tr("No asset breakdown available.")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        detail.byContent.map((row) => (
                          <TableRow key={`${row.contentId}-${row.assetPath}`}>
                            <TableCell>
                              <Typography className="bandwidth-asset-name" title={row.contentName || row.assetPath}>
                                {row.contentName || row.assetPath || tr("Asset")}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <span className={`bandwidth-type-chip ${contentTypeClass(row.contentType)}`}>
                                {row.contentType || tr("unknown")}
                              </span>
                            </TableCell>
                            <TableCell align="right">
                              <span className="bandwidth-hits-badge">{row.hits}</span>
                            </TableCell>
                            <TableCell align="right">
                              <span className={`bandwidth-bytes-badge ${bytesBadgeClass(row.bytes)}`}>
                                {formatBytes(row.bytes)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default ScreenBandwidth;
